import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import { FormParticipantStatusTag } from "@/components/forms/components/FormParticipantStatusTag.tsx";
import { PerformanceReviewCommentForReviewerModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewCommentForReviewerModal.tsx";
import { PerformanceReviewUnsolicitedTag } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewUnsolicitedTag.tsx";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import {
  useParticipantReviewers,
  useParticipantReviewersSave,
} from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { EmployeeFilterDefault } from "@/types/employees/employee.types.ts";
import { ApiFormParticipantStatus } from "@/types/forms/form.types.ts";
import {
  ApiPerformanceReviewReviewer,
  ApiPerformanceReviewReviewerTypes,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  Box,
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Select,
  SelectValueType,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface PerformanceReviewEditReviewersModalProps extends ModalProps {
  performanceReviewId: number;
  employeeId: number;
  reviewerTypeId: number;
  withStatuses?: boolean;
}

export function PerformanceReviewEditReviewersModal({
  performanceReviewId,
  employeeId,
  reviewerTypeId,
  withStatuses = true,
  ...props
}: PerformanceReviewEditReviewersModalProps) {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close, openModal } = useModals();

  const saveParticipantReviewers = useParticipantReviewersSave();

  const { data: participant, isLoading: isLoading } = useParticipantReviewers(
    performanceReviewId,
    employeeId,
    reviewerTypeId
  );

  const [reviewers, setReviewers] = useState<ApiPerformanceReviewReviewer[]>(
    participant?.reviewers ?? []
  );
  const [comments, setComments] = useState<Record<number, string>>({});

  function getExcludeIds() {
    return [...reviewers.map((x) => x.employee.id), ...(participant?.exclude_employee_ids ?? [])];
  }

  const employeesSearch = useEmployeeSearchInline(!!participant, {
    ...EmployeeFilterDefault,
    exclude_ids: getExcludeIds(),
  });

  useEffect(() => {
    if (!participant) return;

    employeesSearch.setFilter({
      ...employeesSearch.filter,
      exclude_ids: getExcludeIds(),
    });
  }, [reviewers, participant]);

  const [syncedParticipant, setSyncedParticipant] = useState<typeof participant>(undefined);
  if (participant !== syncedParticipant) {
    setSyncedParticipant(participant);
    if (participant) {
      setReviewers(participant.reviewers);
      setComments(
        participant.reviewers.reduce(
          (acc, r) => ({ ...acc, [r.employee.id]: r.comment?.text ?? "" }),
          {} as Record<number, string>
        )
      );
    }
  }

  const isManagerType =
    participant?.reviewer_type.type === ApiPerformanceReviewReviewerTypes.manager;

  const handleReviewerAdd = useCallback(
    (item: SelectValueType) => {
      const addMember = employeesSearch.data?.items.find((x) => x.id == item);
      if (!addMember) return;

      if (isManagerType) {
        setReviewers([
          {
            employee: addMember,
            status: ApiFormParticipantStatus.added,
            comment: null,
            id: 0,
            is_unsolicited: false,
          },
        ]);
      } else {
        setReviewers((prev) => [
          ...prev,
          {
            employee: addMember,
            status: ApiFormParticipantStatus.added,
            comment: null,
            id: 0,
            is_unsolicited: false,
          },
        ]);
      }
    },
    [employeesSearch.data, isManagerType]
  );

  const handleReviewerDelete = useCallback((index: number) => {
    setReviewers((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddComment = (employeeId: number) => {
    openModal(PerformanceReviewCommentForReviewerModal, {
      initialComment: comments[employeeId] ?? "",
      onSave: (comment: string) => {
        setComments((prev) => ({ ...prev, [employeeId]: comment }));
      },
    });
  };

  const handleReviewersSave = async () => {
    try {
      await saveParticipantReviewers.mutateAsync({
        id: performanceReviewId,
        employee_id: employeeId,
        reviewer_type_id: reviewerTypeId,
        payload: {
          employees: reviewers.map((x) => ({
            employee_id: x.employee.id,
            comment: comments[x.employee.id] ?? null,
          })),
        },
      });

      close(id);
    } catch {}
  };

  const columns = useMemo<ColumnDef<ApiPerformanceReviewReviewer>[]>(
    () => [
      {
        id: "employee",
        cell: (cell) => (
          <Stack gap="xs">
            <EmployeeItem employee={cell.row.original.employee} />

            {cell.row.original.is_unsolicited && (
              <Stack className={"pl-11"} direction={"horizontal"}>
                <PerformanceReviewUnsolicitedTag />
              </Stack>
            )}
          </Stack>
        ),
        ...ColumnSizes.standard,
      },
      {
        id: "status",
        cell: (cell) =>
          cell.row.original.status && withStatuses ? (
            <FormParticipantStatusTag status={cell.row.original.status} />
          ) : null,
        ...ColumnSizes.narrow,
      },
      {
        id: "comment",
        cell: (cell) => (
          <Box className={"text-ellipsis overflow-hidden whitespace-nowrap cursor-pointer"}>
            <Text
              onClick={() => handleAddComment(cell.row.original.employee.id)}
              className={
                comments[cell.row.original.employee.id]?.length > 0 ? "" : "text-secondary"
              }
            >
              {comments[cell.row.original.employee.id]?.length > 0
                ? comments[cell.row.original.employee.id]
                : t("performance_review:add_specific_focus")}
            </Text>
          </Box>
        ),
        ...ColumnSizes.standard,
      },
      {
        id: "actions",
        cell: (cell) => {
          const index = reviewers.findIndex((r) => r.employee.id === cell.row.original.employee.id);
          return (
            <Box className="flex justify-end">
              <DropdownMenu
                trigger={
                  <ButtonIcon
                    variant="secondary"
                    icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
                  />
                }
              >
                <DropdownMenuItem
                  onClick={() => handleAddComment(cell.row.original.employee.id)}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />}
                  title={t("performance_review:edit_focus")}
                />
                {!isManagerType && (
                  <DropdownMenuItem
                    onClick={() => handleReviewerDelete(index)}
                    className="text-danger"
                    title={t("common:delete")}
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                  />
                )}
              </DropdownMenu>
            </Box>
          );
        },
        ...ColumnSizes.tiny,
      },
    ],
    [reviewers, comments, isManagerType, withStatuses]
  );

  return (
    <Modal
      title={
        isManagerType ? t("performance_review:set_manager") : t("performance_review:select_peers")
      }
      variant={"middle"}
      layout={"base"}
      withCloser={true}
      size={"lg"}
      {...props}
    >
      {!isLoading && participant ? (
        <Stack gap={"2xl"}>
          <EmployeeListItem employee={participant.employee} size={"xl"} />

          <Box key={`reviewer-type-${participant.reviewer_type.id}`}>
            {!isManagerType && (
              <Title
                paddingBottom={true}
                header={
                  (PerformanceReviewReviewerTypeItems().find(
                    (item) => item.id == participant.reviewer_type.type
                  )?.shortTitle ??
                    participant.reviewer_type.title ??
                    "") + ` (${reviewers.length})`
                }
              />
            )}

            <Select
              label={
                isManagerType
                  ? t("performance_review:select_manager")
                  : t("performance_review:add_peer")
              }
              emptyTitle={t("common:select")}
              value={undefined}
              showAvatar={true}
              showCaption={true}
              searchable
              loadOptionsOnOpen
              onSearchOptions={(text) => employeesSearch.search(text)}
              searchOptions={employeesSearch.options}
              onChange={(item) => handleReviewerAdd(item)}
            />

            <Table
              layout="fixed"
              stickyLastColumn={true}
              data={reviewers}
              columns={columns}
              hideHeader={true}
            />
          </Box>

          <Button
            type={"submit"}
            size={"lg"}
            onClick={handleReviewersSave}
            isLoading={saveParticipantReviewers.isPending}
          >
            {t("common:save")}
          </Button>
        </Stack>
      ) : (
        <Skeleton className={"h-30"} />
      )}
    </Modal>
  );
}
